import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { useRoute, useRouter } from 'vue-router';

import logger from '@/logger';
import { sessionService } from '@/services';
import useAutenticacao2faModalStore from '@/stores/useAutenticacao2faModalStore';

const FALLBACK_LISTA_ACORDOS: RouteLocationRaw = {
  path: '/dividas',
  query: { aba: 'acordos' },
};

export type Autenticacao2faPosOtpFailureKind = 'navigate' | 'sync';

export class Autenticacao2faPosOtpError extends Error {
  readonly kind: Autenticacao2faPosOtpFailureKind;
  override readonly cause: unknown;

  constructor(kind: Autenticacao2faPosOtpFailureKind, cause?: unknown) {
    super(
      kind === 'navigate'
        ? 'Falha na navegação após autenticação 2FA'
        : 'Falha ao sincronizar sessão após autenticação 2FA'
    );
    this.name = 'Autenticacao2faPosOtpError';
    this.kind = kind;
    this.cause = cause;
  }
}

export function isAutenticacao2faPosOtpError(error: unknown): error is Autenticacao2faPosOtpError {
  return error instanceof Autenticacao2faPosOtpError;
}

export function useAutenticacao2faGate() {
  const store = useAutenticacao2faModalStore();
  const { isOpen: isModalOpen } = storeToRefs(store);
  const router = useRouter();
  const route = useRoute();

  function abrirOtpParaNavegar(api: string, to: RouteLocationRaw) {
    store.open({ api, onSuccess: { type: 'navigate', to } });
  }

  function abrirOtpParaRevelar(api: string) {
    store.open({ api, onSuccess: { type: 'reveal' } });
  }

  const bounceDeepPageEmAndamento = ref(false);

  async function bounceDeepPageExigindoOtp(api: string, contextoLog?: string) {
    if (bounceDeepPageEmAndamento.value) {
      return;
    }

    bounceDeepPageEmAndamento.value = true;

    try {
      const destino: RouteLocationRaw = route.fullPath;
      abrirOtpParaNavegar(api, destino);

      const back = router.options.history.state?.back;
      const ultimaUrl = typeof back === 'string' ? back : undefined;
      if (ultimaUrl) {
        const rotaAtual = router.resolve(route.fullPath).fullPath;
        const rotaAnterior = router.resolve(ultimaUrl).fullPath;
        if (rotaAnterior !== rotaAtual) {
          await router.replace(ultimaUrl);
          return;
        }
      }

      await router.replace(FALLBACK_LISTA_ACORDOS);
    } catch (error) {
      bounceDeepPageEmAndamento.value = false;
      logger.warn(contextoLog ?? 'Falha no bounce 2FA', error);
    }
  }

  function liberarBounceDeepPage() {
    bounceDeepPageEmAndamento.value = false;
  }

  async function concluirAposOtpValido() {
    const action = store.pendingAction;
    const apiAtual = store.api;

    try {
      sessionService.reload();
      if (!sessionService.is2faAuthorized) {
        throw new Error('Sessão sem otpAutenticado após reload');
      }
    } catch (error) {
      throw new Autenticacao2faPosOtpError('sync', error);
    }

    store.close();

    try {
      if (action?.type === 'navigate') {
        await router.push(action.to);
      }
      store.clearPending();
    } catch (error) {
      if (apiAtual != null) {
        store.open({
          api: apiAtual,
          onSuccess: action ?? { type: 'reveal' },
        });
      }
      throw new Autenticacao2faPosOtpError('navigate', error);
    }
  }

  function fecharOtp() {
    store.close();
    store.clearPending();
  }

  return {
    abrirOtpParaNavegar,
    abrirOtpParaRevelar,
    bounceDeepPageExigindoOtp,
    bounceDeepPageEmAndamento,
    liberarBounceDeepPage,
    concluirAposOtpValido,
    fecharOtp,
    isModalOpen,
  };
}
